import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { AI_PANEL_GRID_REFRESH_EVENT } from "../../../../../components/AIChat/aiPanelCommand";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildConfiguredSourceObjectMetadata,
  buildResourceOperationQueryParams,
  buildSourceObjectCardFields,
  buildSourceObjectValueDisplayResolvers,
  buildWizardValidationError,
  cloneSqlQueryTargetDraft,
  createSqlQueryTargetDraft,
  getFirst,
  getId,
  getLinkedRecord,
  getLinkedRecordItemId,
  getMetadataFields,
  getOperationDisplayName,
  getOperationItem,
  getOperationItems,
  getOperationName,
  getProductResourceDefaults,
  getSourceObjectKey,
  getSourceTargetKey,
  getValue,
  filterSourceTargetsForProductDefaults,
  isSourceObjectTypeQuery,
  isSqlQueryTargetSourceSystem,
  LOAD_SOURCE_OBJECTS_OPERATION,
  MANAGE_SOURCE_OBJECT_OPERATION,
  mergeFilteredSourceTargets,
  requireServiceResponse,
  requireSuccessfulServiceResponse,
  resolveProductResourceEdiTransactionSets,
  resolveSourceTargetEdiTransactionSet,
  resolveSourceTargetMetadata,
  sectionStepExpectsRecord,
  SOURCE_OBJECT_FORM_GROUP_ID,
  SOURCE_OBJECT_ITEM,
  SOURCE_OBJECT_TYPE_QUERY,
  validateRequiredCardGridRows,
  validateSourceTargetRows
} from "../resourceConfigWizardUtils";

const SOURCE_OBJECT_TYPE_VIEW = 1;
const SOURCE_OBJECT_TYPE_TABLE_OR_COLLECTION = 2;
const SOURCE_TARGET_TYPE_FILTER_OPTIONS = [
  { key: "all", title: "All target types", materialIcon: "apps" },
  { key: "table", title: "Table or collection targets", materialIcon: "table_chart", values: [SOURCE_OBJECT_TYPE_TABLE_OR_COLLECTION] },
  { key: "view", title: "View targets", materialIcon: "view_list", values: [SOURCE_OBJECT_TYPE_VIEW] },
  { key: "query", title: "Query targets", materialIcon: "data_object", values: [SOURCE_OBJECT_TYPE_QUERY] }
];

const isCustomQueryTarget = (target) => Boolean(getValue(target, "IsCustomQuery", "isCustomQuery"));

const getSourceTargetOperationId = (target) =>
  getValue(target, "SourceObjectTargetId", "sourceObjectTargetId", "UniqueId", "uniqueId", "_id", "id");

const getSourceObjectTargetReferenceId = (sourceObject) =>
  getValue(sourceObject, "SourceObjectTargetId", "sourceObjectTargetId", "SourceTargetId", "sourceTargetId", "TargetId", "targetId");

const isActiveRecord = (item) => item?.IsActive === true || item?.isActive === true;

const normalizeLookupKey = (value) => String(value || "").trim().toLowerCase();

const dedupeRowsByKey = (rows, getRowKey) => {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  return rows.filter((row, index) => {
    const rawKey = getRowKey?.(row, index);
    const key = String(rawKey === undefined || rawKey === null || rawKey === "" ? `row-${index}` : rawKey).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SourceObjectConfiguratorStep = ({
  context,
  onSave,
  onContinue,
  registerStepController,
  onOpenSourceObjectManagePanel,
  onDirty
}) => {
  const { wizard, values = {}, readOnly, saving, sectionStep, fields, isLastSectionStep } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const sourceSystemMgr = resourceDomainContext?.managers?.sourceSystem;
  const sourceObjectMgr = resourceDomainContext?.managers?.sourceObject;
  const sourceSystemMgrReady = Boolean(sourceSystemMgr);
  const sourceObjectMgrReady = Boolean(sourceObjectMgr);
  const sourceSystemMgrRef = useRef(sourceSystemMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const coreStateRef = useRef(coreState);
  const loadKeyRef = useRef("");
  const confirmContinueRef = useRef(null);
  const prepareStepValuesRef = useRef(null);
  const isBusyRef = useRef(false);
  const pendingSourceTargetKeysRef = useRef(new Set());
  const productResourceDefaults = context.productResourceDefaults || getProductResourceDefaults(wizard);
  const sourceSystemId = getLinkedRecordItemId(getLinkedRecord(wizard, "SourceSystem", "SourceSystem")) || values?.SourceSystemId || "";
  const isSourceObjectMode = sectionStepExpectsRecord(sectionStep, SOURCE_OBJECT_ITEM);
  const [sourceSystem, setSourceSystem] = useState(null);
  const [sourceObjects, setSourceObjects] = useState([]);
  const [selectedTargetKey, setSelectedTargetKey] = useState("");
  const [selectedSourceObjectKey, setSelectedSourceObjectKey] = useState("");
  const [cardEditorOpen, setCardEditorOpen] = useState(false);
  const [sourceTargetMetadata, setSourceTargetMetadata] = useState(null);
  const [sourceObjectMetadata, setSourceObjectMetadata] = useState(null);
  const [loadingConfigurator, setLoadingConfigurator] = useState(true);
  const [sourceObjectRefreshKey, setSourceObjectRefreshKey] = useState(0);
  const [sourceObjectGenerationConfirm, setSourceObjectGenerationConfirm] = useState(null);
  const [localError, setLocalError] = useState("");
  const targetFields = useMemo(() => getMetadataFields(sourceTargetMetadata), [sourceTargetMetadata]);
  const configuredSourceObjectMetadata = useMemo(
    () => buildConfiguredSourceObjectMetadata(sourceObjectMetadata, fields),
    [fields, sourceObjectMetadata]
  );
  const sourceObjectFields = useMemo(
    () => getMetadataFields(configuredSourceObjectMetadata),
    [configuredSourceObjectMetadata]
  );
  const sourceObjectCardFields = useMemo(
    () => buildSourceObjectCardFields(sourceObjectFields),
    [sourceObjectFields]
  );
  const sourceObjectManagerLabel = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    const manageOperation = operations.find((operation) => getOperationName(operation) === MANAGE_SOURCE_OBJECT_OPERATION);
    return getOperationDisplayName(manageOperation) || "Manage Source";
  }, [coreContext?.Operations]);

  useEffect(() => {
    sourceSystemMgrRef.current = sourceSystemMgr;
  }, [sourceSystemMgr]);

  useEffect(() => {
    sourceObjectMgrRef.current = sourceObjectMgr;
  }, [sourceObjectMgr]);

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  useEffect(() => {
    setCardEditorOpen(false);
  }, [isSourceObjectMode]);

  useEffect(() => {
    if (!isSourceObjectMode || typeof window === "undefined") return undefined;

    const handleSourceObjectRefresh = (event) => {
      const detail = event?.detail ?? {};
      if (detail.itemName !== "SourceObject") return;
      const detailSourceSystemId = detail.sourceSystemId || detail.SourceSystemId || "";
      if (detailSourceSystemId && sourceSystemId && String(detailSourceSystemId).toLowerCase() !== String(sourceSystemId).toLowerCase()) {
        return;
      }
      event.preventDefault?.();
      setSourceObjectRefreshKey((previous) => previous + 1);
    };

    window.addEventListener(AI_PANEL_GRID_REFRESH_EVENT, handleSourceObjectRefresh);
    return () => window.removeEventListener(AI_PANEL_GRID_REFRESH_EVENT, handleSourceObjectRefresh);
  }, [isSourceObjectMode, sourceSystemId]);

  useEffect(() => {
    let mounted = true;
    const activeSourceSystemMgr = sourceSystemMgrRef.current;
    const activeSourceObjectMgr = sourceObjectMgrRef.current;
    const loadKey = `${isSourceObjectMode ? "objects" : "targets"}:${sourceSystemId || "none"}:${sourceObjectRefreshKey}`;

    if (!sourceSystemMgrReady || !activeSourceSystemMgr || !sourceSystemId) {
      setLoadingConfigurator(false);
      setSourceSystem(null);
      setSourceObjects([]);
      setSourceTargetMetadata(null);
      if (!sourceSystemMgrReady || !activeSourceSystemMgr) {
        setLocalError("Source System service is not available.");
      }
      loadKeyRef.current = "";
      return undefined;
    }
    if (isSourceObjectMode && (!sourceObjectMgrReady || !activeSourceObjectMgr)) {
      setLoadingConfigurator(false);
      setSourceSystem(null);
      setSourceObjects([]);
      setSourceTargetMetadata(null);
      setLocalError("Source Object service is not available.");
      loadKeyRef.current = "";
      return undefined;
    }

    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoadingConfigurator(true);
    setLocalError("");

    const loadConfiguratorData = async () => {
      try {
        const [sourceSystemResult, sourceSystemMetadataResult] = await Promise.all([
          activeSourceSystemMgr.Query({ model: "x => x.IsDeleted == false" }),
          isSourceObjectMode ? Promise.resolve(null) : activeSourceSystemMgr.GetMetadata({ forceReload: true })
        ]);
        if (!mounted) return;
        requireSuccessfulServiceResponse(sourceSystemResult, "Source System records could not be loaded.", coreStateRef.current);

        if (!isSourceObjectMode) {
          requireServiceResponse(sourceSystemMetadataResult, "Source Target metadata could not be loaded.");
          const resolvedSourceTargetMetadata = resolveSourceTargetMetadata(sourceSystemMetadataResult);
          if (!resolvedSourceTargetMetadata) {
            throw new Error("Source Target metadata could not be loaded.");
          }
          setSourceTargetMetadata(resolvedSourceTargetMetadata);
        } else {
          setSourceTargetMetadata(null);
        }

        const matchedSourceSystem = getOperationItems(sourceSystemResult).find((item) => String(getId(item)) === String(sourceSystemId));
        if (!matchedSourceSystem) {
          setSourceSystem(null);
          setSourceObjects([]);
          setSourceTargetMetadata(null);
          setLocalError("The linked Source System could not be loaded.");
          return;
        }

        const resolvedSourceSystem = matchedSourceSystem;

        const targets = dedupeRowsByKey(getValue(resolvedSourceSystem, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey);
        setSourceSystem({
          ...resolvedSourceSystem,
          SourceObjectTargets: targets
        });
        setSelectedTargetKey((previous) => {
          if (previous && targets.some((target, index) => String(getSourceTargetKey(target, index)) === String(previous))) {
            return previous;
          }
          return "";
        });

        if (isSourceObjectMode && activeSourceObjectMgr) {
          const [sourceObjectResult, metadataResult] = await Promise.all([
            activeSourceObjectMgr.Query({
              model: "x => x.IsDeleted == false && x.SourceSystemId == sourceSystemId",
              params: { sourceSystemId }
            }),
            activeSourceObjectMgr.GetMetadata({})
          ]);
          if (!mounted) return;
          requireSuccessfulServiceResponse(sourceObjectResult, "Source Objects could not be loaded.", coreStateRef.current);
          requireServiceResponse(metadataResult, "Source Object metadata could not be loaded.");

          const matchingSourceObjects = dedupeRowsByKey(
            getOperationItems(sourceObjectResult)
              .filter((item) => String(getValue(item, "SourceSystemId", "sourceSystemId") || "").toLowerCase() === String(sourceSystemId).toLowerCase()),
            getSourceObjectKey
          );
          setSourceObjects(matchingSourceObjects);
          setSourceObjectMetadata(getFirst(metadataResult?.Items) || getFirst(metadataResult) || getFirst(activeSourceObjectMgr.uiMetadata) || null);
          setSelectedSourceObjectKey((previous) => {
            if (previous && matchingSourceObjects.some((item, index) => String(getSourceObjectKey(item, index)) === String(previous))) {
              return previous;
            }
            return "";
          });
        } else {
          setSourceObjects([]);
          setSourceObjectMetadata(null);
        }
      } catch (ex) {
        if (mounted) {
          setSourceSystem(null);
          setSourceObjects([]);
          setSourceTargetMetadata(null);
          setLocalError(ex?.message || (isSourceObjectMode ? "Source Objects could not be loaded." : "Source Targets could not be loaded."));
        }
      } finally {
        if (mounted) setLoadingConfigurator(false);
      }
    };

    void loadConfiguratorData();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) {
        loadKeyRef.current = "";
      }
    };
  }, [isSourceObjectMode, sourceObjectMgrReady, sourceObjectRefreshKey, sourceSystemId, sourceSystemMgrReady, wizard]);

  const productResourceDefaultsKey = JSON.stringify(productResourceDefaults || {});
  const allSourceTargets = useMemo(
    () => dedupeRowsByKey(getValue(sourceSystem, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey),
    [sourceSystem]
  );
  const sourceTargets = useMemo(
    () => filterSourceTargetsForProductDefaults(allSourceTargets, productResourceDefaults),
    // productResourceDefaults is object-recreated by the parent; the serialized key is the stable filter input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSourceTargets, productResourceDefaultsKey]
  );
  const productEdiTransactionSets = useMemo(
    () => resolveProductResourceEdiTransactionSets(productResourceDefaults),
    // productResourceDefaults is object-recreated by the parent; the serialized key is the stable filter input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productResourceDefaultsKey]
  );
  const sourceObjectValueDisplayResolvers = useMemo(
    () => buildSourceObjectValueDisplayResolvers({ sourceSystem, sourceSystemId, sourceTargets, sourceObjects }),
    [sourceObjects, sourceSystem, sourceSystemId, sourceTargets]
  );
  const visibleSourceObjects = useMemo(() => {
    if (!isSourceObjectMode || !sourceObjects.length || !sourceTargets.length || !productEdiTransactionSets.length) {
      return sourceObjects;
    }

    const visibleTargetIds = new Set(sourceTargets.map(getSourceTargetOperationId).map(normalizeLookupKey).filter(Boolean));
    const visibleTransactionSets = new Set(sourceTargets.map(resolveSourceTargetEdiTransactionSet).map(normalizeLookupKey).filter(Boolean));
    if (!visibleTargetIds.size && !visibleTransactionSets.size) return sourceObjects;

    return sourceObjects.filter((sourceObject) => {
      const sourceObjectTargetId = normalizeLookupKey(getSourceObjectTargetReferenceId(sourceObject));
      if (sourceObjectTargetId) return visibleTargetIds.has(sourceObjectTargetId);

      const sourceObjectTransactionSet = normalizeLookupKey(resolveSourceTargetEdiTransactionSet(sourceObject));
      return sourceObjectTransactionSet ? visibleTransactionSets.has(sourceObjectTransactionSet) : true;
    });
  }, [isSourceObjectMode, productEdiTransactionSets.length, sourceObjects, sourceTargets]);

  useEffect(() => {
    if (!selectedTargetKey) return;
    if (sourceTargets.some((target, index) => String(getSourceTargetKey(target, index)) === String(selectedTargetKey))) return;
    setSelectedTargetKey("");
  }, [selectedTargetKey, sourceTargets]);

  useEffect(() => {
    if (!selectedSourceObjectKey) return;
    if (visibleSourceObjects.some((sourceObject, index) => String(getSourceObjectKey(sourceObject, index)) === String(selectedSourceObjectKey))) return;
    setSelectedSourceObjectKey("");
  }, [selectedSourceObjectKey, visibleSourceObjects]);

  const updateSourceTarget = useCallback((updatedTarget) => {
    onDirty?.();
    pendingSourceTargetKeysRef.current.delete(String(getSourceTargetKey(updatedTarget)));
    setSourceSystem((previous) => {
      if (!previous) return previous;
      const currentTargets = dedupeRowsByKey(getValue(previous, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey);
      const nextTargets = currentTargets.map((target, index) =>
        String(getSourceTargetKey(target, index)) === String(getSourceTargetKey(updatedTarget, index))
          ? updatedTarget
          : target
      );
      return {
        ...previous,
        SourceObjectTargets: nextTargets
      };
    });
  }, [onDirty]);

  const addQueryTarget = useCallback(() => {
    if (!sourceSystem || !isSqlQueryTargetSourceSystem(sourceSystem)) return;
    const draftTarget = createSqlQueryTargetDraft(sourceSystem, sourceTargets);
    const draftKey = getSourceTargetKey(draftTarget, sourceTargets.length);
    pendingSourceTargetKeysRef.current.add(String(draftKey));
    onDirty?.();
    setSourceSystem((previous) => previous
      ? {
        ...previous,
        SourceObjectTargets: [
          ...dedupeRowsByKey(getValue(previous, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey),
          draftTarget
        ]
      }
      : previous);
    setSelectedTargetKey(draftKey);
  }, [onDirty, sourceSystem, sourceTargets]);

  const cloneQueryTarget = useCallback((target) => {
    if (!sourceSystem || !isSqlQueryTargetSourceSystem(sourceSystem) || !isSourceObjectTypeQuery(target)) return;
    const draftTarget = cloneSqlQueryTargetDraft(sourceSystem, sourceTargets, target);
    const draftKey = getSourceTargetKey(draftTarget, sourceTargets.length);
    pendingSourceTargetKeysRef.current.add(String(draftKey));
    onDirty?.();
    setSourceSystem((previous) => previous
      ? {
        ...previous,
        SourceObjectTargets: [
          ...dedupeRowsByKey(getValue(previous, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey),
          draftTarget
        ]
      }
      : previous);
    setSelectedTargetKey(draftKey);
  }, [onDirty, sourceSystem, sourceTargets]);

  const cancelSourceTargetEdit = useCallback((row, key) => {
    const targetKey = String(key || getSourceTargetKey(row));
    if (!pendingSourceTargetKeysRef.current.has(targetKey)) return;
    pendingSourceTargetKeysRef.current.delete(targetKey);
    setSourceSystem((previous) => {
      if (!previous) return previous;
      const nextTargets = dedupeRowsByKey(getValue(previous, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey)
        .filter((target, index) => String(getSourceTargetKey(target, index)) !== targetKey);
      return {
        ...previous,
        SourceObjectTargets: nextTargets
      };
    });
    setSelectedTargetKey("");
  }, []);

  const targetHeaderIconActions = useMemo(() => (
    !isSourceObjectMode && isSqlQueryTargetSourceSystem(sourceSystem)
      ? [{
        Key: "clone-query-target",
        Title: "Clone Query Target",
        iconKey: "clone",
        iconSize: "1.275rem",
        visible: (row) => isSourceObjectTypeQuery(row) && !isCustomQueryTarget(row),
        ActionCallback: (row) => cloneQueryTarget(row)
      }]
      : []
  ), [cloneQueryTarget, isSourceObjectMode, sourceSystem]);
  const targetToolbarActions = useMemo(() => (
    !isSourceObjectMode && isSqlQueryTargetSourceSystem(sourceSystem)
      ? [{
        Key: "add-query-target",
        Title: "Add Query Target",
        Label: "Add Query Target",
        IconKey: "add",
        Disabled: readOnly || saving || loadingConfigurator || !sourceSystemId,
        ActionCallback: addQueryTarget
      }]
      : []
  ), [addQueryTarget, isSourceObjectMode, loadingConfigurator, readOnly, saving, sourceSystem, sourceSystemId]);

  const updateSourceObject = useCallback((updatedSourceObject) => {
    onDirty?.();
    setSourceObjects((previous) => dedupeRowsByKey(previous, getSourceObjectKey).map((sourceObject, index) =>
      String(getSourceObjectKey(sourceObject, index)) === String(getSourceObjectKey(updatedSourceObject, index))
        ? updatedSourceObject
        : sourceObject
    ));
  }, [onDirty]);

  const saveSourceTargets = useCallback(async () => {
    if (!sourceSystem) throw new Error("Complete the Source System step before configuring Source Targets.");
    const activeMgr = sourceSystemMgrRef.current;
    if (!activeMgr) throw new Error("Source System service is not available.");

    const validationMessage = validateSourceTargetRows(sourceTargets, targetFields, "Source Target");
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");

    const sourceSystemToSave = {
      ...sourceSystem,
      IsNew: false,
      SourceObjectTargets: mergeFilteredSourceTargets(allSourceTargets, sourceTargets)
    };
    const saveResult = await activeMgr.saveItem(sourceSystemToSave);
    requireSuccessfulServiceResponse(saveResult, "Source Targets could not be saved.", coreStateRef.current);

    const savedSourceSystem = getOperationItem(saveResult)
      || getFirst(saveResult?.Items)
      || getFirst(saveResult?.items)
      || saveResult?.Item
      || saveResult?.item
      || sourceSystemToSave;
    const savedTargets = dedupeRowsByKey(getValue(savedSourceSystem, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey);
    const uniqueSavedSourceSystem = {
      ...savedSourceSystem,
      SourceObjectTargets: savedTargets
    };
    setSourceSystem(uniqueSavedSourceSystem);
    return uniqueSavedSourceSystem;
  }, [allSourceTargets, sourceSystem, sourceTargets, targetFields]);

  const loadSourceObjectsForSourceSystem = useCallback(async () => {
    const activeMgr = sourceObjectMgrRef.current;
    if (!activeMgr) return [];

    const sourceObjectResult = await activeMgr.Query({
      model: "x => x.IsDeleted == false && x.SourceSystemId == sourceSystemId",
      params: { sourceSystemId }
    });
    requireSuccessfulServiceResponse(sourceObjectResult, "Source Objects could not be loaded.", coreStateRef.current);

    const matchingSourceObjects = dedupeRowsByKey(
      getOperationItems(sourceObjectResult)
        .filter((item) => String(getValue(item, "SourceSystemId", "sourceSystemId") || "").toLowerCase() === String(sourceSystemId).toLowerCase()),
      getSourceObjectKey
    );
    setSourceObjects(matchingSourceObjects);
    return matchingSourceObjects;
  }, [sourceSystemId]);

  const runLoadSourceObjects = useCallback(async (sourceSystemToLoad) => {
    const activeMgr = sourceSystemMgrRef.current;
    if (!activeMgr?.RunOperation) throw new Error("Source System service is not available.");

    const activeTargetIds = sourceTargets
      .filter(isActiveRecord)
      .map(getSourceTargetOperationId)
      .filter(Boolean);
    if (!activeTargetIds.length) return [];

    const result = await activeMgr.RunOperation({
      operationName: LOAD_SOURCE_OBJECTS_OPERATION,
      item: sourceSystemToLoad,
      queryParams: buildResourceOperationQueryParams({
        queryParams: {
          targetId: activeTargetIds.join(","),
          reload: true
        },
        wizard,
        item: sourceSystemToLoad
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    requireSuccessfulServiceResponse(result, "Source Objects could not be generated.", coreStateRef.current);

    const operationSourceObjects = getOperationItems(result);
    const refreshedSourceObjects = await loadSourceObjectsForSourceSystem();
    const mergedSourceObjects = dedupeRowsByKey([
      ...refreshedSourceObjects,
      ...operationSourceObjects
    ], getSourceObjectKey);
    setSourceObjects(mergedSourceObjects);
    return mergedSourceObjects;
  }, [loadSourceObjectsForSourceSystem, sourceTargets, wizard]);

  const saveSourceObjects = useCallback(async () => {
    const activeMgr = sourceObjectMgrRef.current;
    if (!activeMgr) throw new Error("Source Object service is not available.");

    const validationMessage = validateRequiredCardGridRows(visibleSourceObjects, sourceObjectFields, "Source Object");
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");

    const savedSourceObjects = [];
    for (const sourceObject of sourceObjects) {
      const sourceObjectToSave = {
        ...sourceObject,
        IsNew: !getId(sourceObject),
        SourceSystemId: sourceSystemId || getValue(sourceObject, "SourceSystemId", "sourceSystemId")
      };
      const saveResult = await activeMgr.saveItem(sourceObjectToSave);
      requireSuccessfulServiceResponse(saveResult, "Source Object could not be saved.", coreStateRef.current);

      savedSourceObjects.push(
        getOperationItem(saveResult)
        || getFirst(saveResult?.Items)
        || getFirst(saveResult?.items)
        || saveResult?.Item
        || saveResult?.item
        || sourceObjectToSave
      );
    }

    const uniqueSavedSourceObjects = dedupeRowsByKey(savedSourceObjects, getSourceObjectKey);
    setSourceObjects(uniqueSavedSourceObjects);
    return uniqueSavedSourceObjects;
  }, [sourceObjectFields, sourceObjects, sourceSystemId, visibleSourceObjects]);

  const confirmContinue = useCallback(() => {
    if (isSourceObjectMode) return true;
    return new Promise((resolve) => {
      setSourceObjectGenerationConfirm({ resolve });
    });
  }, [isSourceObjectMode]);

  const closeSourceObjectGenerationConfirm = useCallback((confirmed) => {
    const resolver = sourceObjectGenerationConfirm?.resolve;
    setSourceObjectGenerationConfirm(null);
    resolver?.(confirmed);
  }, [sourceObjectGenerationConfirm]);

  const prepareStepValues = useCallback(async (currentValues = values, options = {}) => {
    if (isSourceObjectMode) {
      const savedSourceObjects = await saveSourceObjects();
      return {
        ...currentValues,
        SourceSystemId: sourceSystemId,
        SourceObjectCount: savedSourceObjects.length,
        ActiveSourceObjectCount: savedSourceObjects.filter((item) => item?.IsActive === true || item?.isActive === true).length
      };
    }

    const savedSourceSystem = await saveSourceTargets();
    const targets = dedupeRowsByKey(getValue(savedSourceSystem, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetKey);
    const loadedSourceObjects = options.action === "continue"
      ? await runLoadSourceObjects(savedSourceSystem)
      : [];

    return {
      ...currentValues,
      SourceSystemId: sourceSystemId,
      SourceTargetCount: targets.length,
      ActiveSourceTargetCount: targets.filter((target) => target?.IsActive === true || target?.isActive === true).length,
      SourceObjectCount: options.action === "continue" ? loadedSourceObjects.length : getValue(currentValues, "SourceObjectCount", "sourceObjectCount") || 0,
      ActiveSourceObjectCount: options.action === "continue"
        ? loadedSourceObjects.filter(isActiveRecord).length
        : getValue(currentValues, "ActiveSourceObjectCount", "activeSourceObjectCount") || 0
    };
  }, [isSourceObjectMode, runLoadSourceObjects, saveSourceObjects, saveSourceTargets, sourceSystemId, values]);

  confirmContinueRef.current = confirmContinue;
  prepareStepValuesRef.current = prepareStepValues;
  isBusyRef.current = loadingConfigurator;

  useEffect(() => {
    registerStepController?.({
      confirmContinue: () => confirmContinueRef.current?.() ?? true,
      skipStepInputValidation: true,
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "save" }) ?? currentValues,
      prepareContinue: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "continue" }) ?? currentValues
    });
    return () => registerStepController?.(null);
  }, [registerStepController]);

  const title = isSourceObjectMode ? "Source Objects" : "Source Targets";
  const description = isSourceObjectMode
    ? "Review generated source objects and configure the fields exposed by this linked setup."
    : "Activate the required targets for this linked setup. Search the generated targets, then use the quick activate control or click a card to review and update it.";

  return (
    <div className="resource-wizard__linked-record-catalog">
      <div className="resource-wizard__linked-record-catalog-header">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {localError ? <div className="resource-wizard__alert">{localError}</div> : null}
      <div className="resource-wizard__linked-record-catalog-scroll">
        {!sourceSystemId ? (
          <div className="resource-wizard__state">Complete the Source System step before configuring {title}.</div>
        ) : loadingConfigurator ? (
          <div className="resource-wizard__linked-step-loading"><Loading /></div>
        ) : isSourceObjectMode ? (
          <CardGridControl
            rows={visibleSourceObjects}
            metadata={configuredSourceObjectMetadata}
            formMetadata={configuredSourceObjectMetadata}
            formFields={sourceObjectFields}
            cardFields={sourceObjectCardFields}
            rowKey={getSourceObjectKey}
            selectedRowKey={selectedSourceObjectKey}
            onSelectedRowKeyChange={setSelectedSourceObjectKey}
            onEditingChange={setCardEditorOpen}
            onRowChange={updateSourceObject}
            headerIconActions={readOnly ? [] : [{
              Key: "manage-source-object",
              Title: sourceObjectManagerLabel,
              IconKey: "settings",
              ActionCallback: (row) => onOpenSourceObjectManagePanel?.(row)
            }]}
            readOnly={readOnly}
            toggleEnabled={false}
            toggleFieldName="IsActive"
            toggleActiveLabel="Deactivate source object"
            toggleInactiveLabel="Activate source object"
            activeFilterFieldName="IsActive"
            formGroupId={SOURCE_OBJECT_FORM_GROUP_ID}
            valueDisplayResolvers={sourceObjectValueDisplayResolvers}
            emptyContent={`No Source Objects have been generated for ${productResourceDefaults.providerName || "this Source System"}. Return to Source Targets and use Save & Continue to generate source objects.`}
            minCardWidth="15rem"
          />
        ) : !sourceTargetMetadata ? (
          <div className="resource-wizard__state">Source Target metadata could not be loaded.</div>
        ) : (
          <CardGridControl
            rows={sourceTargets}
            metadata={sourceTargetMetadata}
            formMetadata={sourceTargetMetadata}
            formFields={targetFields}
            cardFields={targetFields.filter((field) => field.ShowOnCard)}
            rowKey={getSourceTargetKey}
            selectedRowKey={selectedTargetKey}
            onSelectedRowKeyChange={setSelectedTargetKey}
            onEditingChange={setCardEditorOpen}
            onCancelEdit={cancelSourceTargetEdit}
            onRowChange={updateSourceTarget}
            headerIconActions={targetHeaderIconActions}
            toolbarActions={targetToolbarActions}
            readOnly={readOnly}
            toggleEnabled={true}
            toggleFieldName="IsActive"
            toggleActiveLabel="Deactivate target"
            toggleInactiveLabel="Activate target"
            activeFilterFieldName="IsActive"
            typeFilterEnabled={true}
            typeFilterFieldName="ObjectType"
            typeFilterOptions={SOURCE_TARGET_TYPE_FILTER_OPTIONS}
            emptyContent={`No Source Targets have been generated for ${productResourceDefaults.providerName || "this Source System"}. Return to Source System and use Save & Continue to generate targets.`}
            minCardWidth="15rem"
          />
        )}
      </div>
      {!cardEditorOpen ? (
        <div className="resource-wizard__linked-step-actions">
          <div className="resource-wizard__linked-step-actions-left" />
          <div className="resource-wizard__linked-step-actions-right">
            <ButtonControl
              type="standard-small"
              className="resource-wizard__button resource-wizard__button--secondary"
              onClick={() => onSave?.()}
              disabled={readOnly || saving || loadingConfigurator || !sourceSystemId}
              loading={saving}
            >
              Save
            </ButtonControl>
            {!isLastSectionStep ? (
              <ButtonControl
                type="ok-small"
                className="resource-wizard__button resource-wizard__button--primary"
                onClick={() => onContinue?.()}
                disabled={readOnly || saving || loadingConfigurator || !sourceSystemId}
                loading={saving}
              >
                Save & Continue
              </ButtonControl>
            ) : null}
          </div>
        </div>
      ) : null}
      <DialogControl
        title="Generate Source Objects"
        openDialog={Boolean(sourceObjectGenerationConfirm)}
        okText="Continue"
        cancelText="Cancel"
        onOk={() => closeSourceObjectGenerationConfirm(true)}
        onCancel={() => closeSourceObjectGenerationConfirm(false)}
        onX={() => closeSourceObjectGenerationConfirm(false)}
      >
        <p>Convoke will save these Source Targets, then generate Source Objects from the active targets. Do you want to continue?</p>
      </DialogControl>
    </div>
  );
};

export default SourceObjectConfiguratorStep;
